import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import annotationDataService from '../services/AnnotationDataService';
import {useNavigate} from "react-router-dom";
// import {calculatePosition, toMusicalPosition} from "../utils/timeUtils";
import {calculatePosition, getGridLevel, toMusicalPosition} from '../utils/annotationUtils';
import { validateInstrumentTracks } from '../utils/instrumentValidation';

// Create context
const AnnotationContext = createContext();

// Provider component
export const AnnotationProvider = ({ children }) => {
  const [annotations, setAnnotations] = useState([]);
  const [selectedAnnotationId, setSelectedAnnotationId] = useState(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const [error, setError] = useState(null);
  const [generalInfo, setGeneralInfo] = useState(null);
  const [keyChanges, setKeyChanges] = useState([]);
  const [tempoChanges, setTempoChanges] = useState([]);
  const [timeSignatures, setTimeSignatures] = useState([]);
  const [activeFormBlockId, setActiveFormBlockId] = useState(null);
  const [instrumentValidationError, setInstrumentValidationError] = useState(null);

  const navigate = useNavigate();

  // Register for data service updates
  useEffect(() => {
    if (!isInitialized) return;

    const unregister = annotationDataService.registerListener(() => {
      // When domain data changes, update UI annotations
      const uiAnnotations = annotationDataService.getAllAnnotationsInUiFormat();
      setAnnotations(uiAnnotations);
      setSelectedAnnotationId(selectedId => (
        selectedId && !uiAnnotations.some(annotation => annotation.id === selectedId)
          ? null
          : selectedId
      ));
      setActiveFormBlockId(blockId => (
        blockId && !uiAnnotations.some(annotation => annotation.id === blockId)
          ? null
          : blockId
      ));

      // Also update general info
      const uiGeneralInfo = annotationDataService.getSongGeneralInfoInUiFormat();
      setGeneralInfo(uiGeneralInfo);

      // Update key changes
      const uiKeyChanges = annotationDataService.getKeyChangesInUiFormat();
      setKeyChanges(uiKeyChanges);

      // Update tempo changes
      const uiTempoChanges = annotationDataService.getTempoChangesInUiFormat();
      setTempoChanges(uiTempoChanges);

      // Update time signatures
      const timeSignaturesData = annotationDataService.getTimeSignaturesInUiFormat();
      setTimeSignatures(timeSignaturesData);
    });

    return unregister;
  }, [isInitialized]);

  // Initialize AnnotationSheet
  const initializeSheet = useCallback(async (sheetId, userId, sessionId) => {
    setActiveFormBlockId(null);
    try {
      try {
        await annotationDataService.initialize(userId, sheetId, sessionId);
        setIsInitialized(true);
      } catch (e) {
        // Show the error and navigate back
        alert("This annotation sheet cannot be accessed at this time, maybe another user is modifying this sheet, try again later");
        console.error(e);
        navigate(`/`);
        return; // Exit early to prevent further processing
      }

      // Convert song general info to UI format
      const uiGeneralInfo = annotationDataService.getSongGeneralInfoInUiFormat();
      setGeneralInfo(uiGeneralInfo);

      // Convert domain annotations to UI format
      const uiAnnotations = annotationDataService.getAllAnnotationsInUiFormat();
      setAnnotations(uiAnnotations);

      // Update key changes
      const uiKeyChanges = annotationDataService.getKeyChangesInUiFormat();
      setKeyChanges(uiKeyChanges);

      // Update tempo changes
      const uiTempoChanges = annotationDataService.getTempoChangesInUiFormat();
      setTempoChanges(uiTempoChanges);

      // Update time signatures
      const timeSignaturesData = annotationDataService.getTimeSignaturesInUiFormat();
      setTimeSignatures(timeSignaturesData);

      // Perform instrument validation
      const annotationSheet = annotationDataService.getAnnotationSheet();
      const validationResult = validateInstrumentTracks(annotationSheet);
      
      if (!validationResult.isValid) {
        console.error('Instrument validation failed:', validationResult.invalidTracks);
        setInstrumentValidationError(validationResult);
        return; // Stop initialization if validation fails
      }

    } catch (err) {
      console.error('Failed to initialize annotation sheet:', err);
      setError('Failed to load annotation data');
    }
  }, [navigate]);

  // Update general information
  const handleSongGeneralInfoUpdate = useCallback((newGeneralInfo) => {
    try {
      // Update in domain model
      // console.log('OSDEBUG handleSongGenAnnotationProvider(2) completed: ', newGeneralInfo.completed);
      annotationDataService.updateSongGeneralInfo(newGeneralInfo);

      // No need to update state directly, the listener will handle it
      return true;
    } catch (err) {
      console.error('Failed to update general info:', err);
      setError('Failed to update general info');
      return false;
    }
  }, []);

  // Create annotation
  const handleAnnotationCreate = useCallback((newAnnotation, options) => {
    try {
      // Create in domain model
      const createdAnnotation = annotationDataService.createAnnotation(newAnnotation, options);

      // No need to update state directly, the listener will handle it
      return createdAnnotation;
    } catch (err) {
      console.error('Failed to create annotation:', err);
      setError('Failed to create annotation');
      return false;
    }
  }, []);

  const handleAnnotationsCreate = useCallback((newAnnotations, options) => {
    try {
      return annotationDataService.createAnnotations(newAnnotations, options);
    } catch (err) {
      console.error('Failed to create annotations:', err);
      setError('Failed to create annotations');
      return { success: false, reason: 'create-failed' };
    }
  }, []);

  // Update annotation
  const handleAnnotationUpdate = useCallback((id, changes, options) => {
    // console.log('OSDEBUG4 [2: handleAnnotationUpdate] change: ', changes);
    try {
      // Update in domain model
      const success = annotationDataService.updateAnnotation(id, changes, undefined, options);

      // No need to update state directly, the listener will handle it
      return success;
    } catch (err) {
      console.error('Failed to update annotation:', err);
      setError('Failed to update annotation');
      return false;
    }
  }, []);

  const handleAnnotationsMove = useCallback((changes, options) => {
    try {
      return annotationDataService.moveAnnotations(changes, options);
    } catch (err) {
      console.error('Failed to move annotations:', err);
      setError('Failed to move annotations');
      return { success: false, reason: 'move-failed' };
    }
  }, []);

  // Delete annotation
  const handleAnnotationDelete = useCallback((id) => {
    try {
      // Delete from domain model
      const success = annotationDataService.deleteAnnotation(id);

      // Clear selected ID if it was the deleted annotation
      if (success && selectedAnnotationId === id) {
        setSelectedAnnotationId(null);
      }
      if (success) {
        setActiveFormBlockId(blockId => blockId === id ? null : blockId);
      }

      // No need to update state directly, the listener will handle it
      return success;
    } catch (err) {
      console.error('Failed to delete annotation:', err);
      setError('Failed to delete annotation');
      return false;
    }
  }, [selectedAnnotationId]);

  const handleAnnotationsDelete = useCallback((ids) => {
    try {
      const result = annotationDataService.deleteAnnotations(ids);
      if (!result.success) return result;

      const deletedIds = new Set(ids);
      if (selectedAnnotationId && deletedIds.has(selectedAnnotationId)) {
        setSelectedAnnotationId(null);
      }
      setActiveFormBlockId(blockId => deletedIds.has(blockId) ? null : blockId);
      return result;
    } catch (err) {
      console.error('Failed to delete annotations:', err);
      setError('Failed to delete annotations');
      return { success: false, reason: 'delete-failed' };
    }
  }, [selectedAnnotationId]);

  // Handle annotation selection
  const handleAnnotationSelect = useCallback((id) => {
    setSelectedAnnotationId(id);
  }, []);

  const openAnnotationForm = useCallback((blockId) => {
    setActiveFormBlockId(blockId);
  }, []);

  const closeAnnotationForm = useCallback((blockId) => {
    setActiveFormBlockId(activeBlockId => (
      activeBlockId === blockId ? null : activeBlockId
    ));
  }, []);

  // Get annotations for a specific track
  const getTrackAnnotations = useCallback((trackId) => {
    return annotations.filter(a => a.trackId === trackId);
  }, [annotations]);

  // Save the annotation sheet
  const saveAnnotationSheet = useCallback(async (user_id, sheet_id, session_id, status) => {
    try {
      await annotationDataService.saveAnnotationSheet(user_id, sheet_id, session_id, status);
      return true;
    } catch (err) {
      console.error('Failed to save annotation sheet:', err);
      return false;
    }
  }, []);

  // Key Change handlers
  const handleKeyChangeAdd = (keyChange) => {
    try {
      const result = annotationDataService.addKeyChange(keyChange);
      return result;
    } catch (error) {
      console.error('Failed to add key change:', error);
      setError('Failed to add key change. Please try again.');
      return { success: false, error: error.message };
    }
  };

  const handleKeyChangeUpdate = (index, updatedKeyChange) => {
    try {
      const result = annotationDataService.updateKeyChange(index, updatedKeyChange);
      return result;
    } catch (error) {
      console.error('Failed to update key change:', error);
      setError('Failed to update key change. Please try again.');
      return { success: false, error: error.message };
    }
  };

  const handleKeyChangeDelete = (index) => {
    try {
      const result = annotationDataService.deleteKeyChange(index);
      return result;
    } catch (error) {
      console.error('Failed to delete key change:', error);
      setError('Failed to delete key change. Please try again.');
      return { success: false, error: error.message };
    }
  };

  const handleBatchKeyChanges = useCallback((keyChanges) => {
    try {
      const result = annotationDataService.applyBatchKeyChanges(keyChanges);
      return result;
    } catch (error) {
      console.error('Failed to apply batch key changes:', error);
      setError('Failed to apply batch key changes. Please try again.');
      return { success: false, error: error.message };
    }
  }, []);

  // Tempo Change handlers
  const handleTempoChangeAdd = (tempoChange) => {
    try {
      const result = annotationDataService.addTempoChange(tempoChange);
      return result;
    } catch (error) {
      console.error('Failed to add tempo change:', error);
      setError('Failed to add tempo change. Please try again.');
      return { success: false, error: error.message };
    }
  };

  const handleTempoChangeUpdate = (index, updatedTempoChange) => {
    try {
      const result = annotationDataService.updateTempoChange(index, updatedTempoChange);
      return result;
    } catch (error) {
      console.error('Failed to update tempo change:', error);
      setError('Failed to update tempo change. Please try again.');
      return { success: false, error: error.message };
    }
  };

  const handleTempoChangeDelete = (index) => {
    try {
      const result = annotationDataService.deleteTempoChange(index);
      return result;
    } catch (error) {
      console.error('Failed to delete tempo change:', error);
      setError('Failed to delete tempo change. Please try again.');
      return { success: false, error: error.message };
    }
  };

  const handleBatchTempoChanges = useCallback((tempoChanges) => {
    try {
      const result = annotationDataService.applyBatchTempoChanges(tempoChanges);
      return result;
    } catch (error) {
      console.error('Failed to apply batch tempo changes:', error);
      setError('Failed to apply batch tempo changes. Please try again.');
      return { success: false, error: error.message };
    }
  }, []);

  // Handle time signature operations
  const handleTimeSignatureAdd = useCallback((timeSignatureData) => {
    try {
      const result = annotationDataService.addTimeSignature(timeSignatureData);
      return result;
    } catch (error) {
      console.error('Failed to add time signature:', error);
      setError('Failed to add time signature. Please try again.');
      return { success: false, error: error.message };
    }
  }, []);

  const handleTimeSignatureUpdate = useCallback((index, timeSignatureData) => {
    try {
      const result = annotationDataService.updateTimeSignature(index, timeSignatureData);
      return result;
    } catch (error) {
      console.error('Failed to update time signature:', error);
      setError('Failed to update time signature. Please try again.');
      return { success: false, error: error.message };
    }
  }, []);

  const handleTimeSignatureDelete = useCallback((index) => {
    try {
      const result = annotationDataService.deleteTimeSignature(index);
      return result;
    } catch (error) {
      console.error('Failed to delete time signature:', error);
      setError('Failed to delete time signature. Please try again.');
      return { success: false, error: error.message };
    }
  }, []);

  const handleBatchTimeSignatureChanges = useCallback((timeSignatureRanges) => {
    try {
      const result = annotationDataService.applyBatchTimeSignatureChanges(timeSignatureRanges);
      return result;
    } catch (error) {
      console.error('Failed to apply batch time signature changes:', error);
      setError('Failed to apply batch time signature changes. Please try again.');
      return { success: false, error: error.message };
    }
  }, []);

  // Context value
  const contextValue = {
    annotations,
    selectedAnnotationId,
    error,
    initializeSheet,
    isInitialized,
    activeFormBlockId,
    isFormOpen: activeFormBlockId !== null,
    openAnnotationForm,
    closeAnnotationForm,
    handleAnnotationCreate,
    handleAnnotationsCreate,
    handleAnnotationUpdate,
    handleAnnotationsMove,
    handleAnnotationSelect,
    handleAnnotationDelete,
    handleAnnotationsDelete,
    getTrackAnnotations,
    saveAnnotationSheet,
    // General info
    generalInfo,
    handleSongGeneralInfoUpdate,
    // Key changes
    keyChanges,
    handleKeyChangeAdd,
    handleKeyChangeUpdate,
    handleKeyChangeDelete,
    handleBatchKeyChanges,
    // Tempo changes
    tempoChanges,
    handleTempoChangeAdd,
    handleTempoChangeUpdate,
    handleTempoChangeDelete,
    handleBatchTempoChanges,
    // Time signatures
    timeSignatures,
    handleTimeSignatureAdd,
    handleTimeSignatureUpdate,
    handleTimeSignatureDelete,
    handleBatchTimeSignatureChanges,
    // Instrument validation
    instrumentValidationError
  };

  return (
    <AnnotationContext.Provider value={contextValue}>
      {children}
    </AnnotationContext.Provider>
  );
};

// Custom hook for using the context
export const useAnnotation = () => {
  const context = useContext(AnnotationContext);
  if (context === undefined) {
    throw new Error('useAnnotation must be used within an AnnotationProvider');
  }
  return context;
};

export default AnnotationContext;
